/**
 * @fileoverview Modal de checkout self-service no fim do teste grátis.
 * 3 etapas: (1) Plano — seleção + cotação ao vivo; (2) Cobrança — CPF/CNPJ,
 * telefone e endereço (production-ready, antifraude); (3) Pagamento — cartão
 * tokenizado NO BROWSER (endpoint /tokens?appId=chave_publica; dado do cartão
 * nunca passa pelo nosso servidor) → POST /account/checkout cria a assinatura.
 * Aparece quando o trial acabou (status aguarda ou pagamento vencido), p/ admin.
 */

import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Dialog, makeStyles, Tooltip } from "@material-ui/core";
import { Minus, Plus, Sparkles, Check, CreditCard, Loader, ArrowLeft, ArrowRight, CheckCircle2, LogOut, HelpCircle, MessageCircle, Copy, QrCode } from "lucide-react";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";

import api from "../../../services/api";
import { tokenizeCard, hasPagarmePublicKey } from "../../../utils/cardForm";
import { i18n } from "../../../translate/i18n";
import { createModalDesign } from "../../../styles/modalDesign";
import { useOnboarding } from "../../../context/Onboarding/OnboardingContext";
import { AuthContext } from "../../../context/Auth/AuthContext";

// Suporte da Sttelar no WhatsApp (+55 11 92622-8406) — abre em nova aba na etapa
// de plano, com mensagem pré-preenchida.
const WHATSAPP_SUPPORT_URL =
  "https://wa.me/5511926228406?text=" +
  encodeURIComponent(i18n.t("onboarding.checkout.supportWhatsappMessage"));
const STEP_INDEX = { select: 0, billing: 1, card: 2, pix: 2, success: 2 };

// Fallback de cópia p/ navegadores sem Clipboard API (contexto não-seguro,
// WebView antigo etc.) — textarea oculta + execCommand. Retorna true se copiou.
const legacyCopy = text => {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const ACCEPTED_BRANDS = [
  { key: "visa", label: "VISA" },
  { key: "mastercard", label: "Mastercard" },
  { key: "amex", label: "Amex" },
  { key: "elo", label: "Elo" },
  { key: "hipercard", label: "Hipercard" },
  { key: "diners", label: "Diners" }
];

const fmtBRL = cents =>
  cents == null ? "—" : (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const onlyDigits = s => (s || "").replace(/\D/g, "");

function applyMask(digits, mask) {
  let out = "", di = 0;
  for (const ch of mask) {
    if (di >= digits.length) break;
    if (ch === "#") out += digits[di++];
    else out += ch;
  }
  return out;
}
const fmtDoc = v => {
  const d = onlyDigits(v).slice(0, 14);
  return applyMask(d, d.length <= 11 ? "###.###.###-##" : "##.###.###/####-##");
};
const fmtPhone = v => {
  const d = onlyDigits(v).slice(0, 11);
  return applyMask(d, d.length <= 10 ? "(##) ####-####" : "(##) #####-####");
};
const fmtCep = v => applyMask(onlyDigits(v).slice(0, 8), "#####-###");

function detectBrand(number) {
  const n = onlyDigits(number);
  if (!n) return null;
  if (/^4/.test(n)) return "visa";
  if (/^3[47]/.test(n)) return "amex";
  if (/^(606282|3841)/.test(n)) return "hipercard";
  if (/^(4011|4312|4389|4514|4576|5041|5066|5067|509|627780|636297|636368|650|6516|6550)/.test(n)) return "elo";
  if (/^3(0[0-5]|6|8)/.test(n)) return "diners";
  if (/^(5[1-5]|2(2[2-9]|[3-6][0-9]|7[01]|720))/.test(n)) return "mastercard";
  return null;
}
function maskedNumber(number) {
  const n = onlyDigits(number).slice(0, 19);
  const padded = n.padEnd(Math.max(16, n.length), "•");
  return (padded.match(/.{1,4}/g) || []).join(" ");
}

const useStyles = makeStyles(theme => {
  const md = createModalDesign(theme);
  const dark = theme.palette.type === "dark";
  const border = dark ? "rgba(255,255,255,0.1)" : "#e5e7eb";
  return {
    dialog: {
      ...md.mdDialog,
      "& .MuiDialog-paper": { ...md.mdDialog["& .MuiDialog-paper"], width: "min(620px, calc(100vw - 32px))", maxHeight: "calc(100vh - 40px)" },
      // Backdrop do paywall: fundo escuro quase OPACO (ilegível, custo ~zero).
      // SEM backdrop-filter de propósito — o blur fazia o navegador recompor a
      // tela inteira a cada tecla digitada no formulário (lag de digitação). A
      // opacidade alta já entrega o "não-dá-pra-ler". Seletor descendente p/
      // vencer o backdrop padrão do MUI.
      "& .MuiBackdrop-root": {
        backgroundColor: "rgba(8, 10, 20, 0.92)"
      }
    },
    head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: theme.spacing(3.5, 3.5, 0) },
    title: { fontSize: "1.3rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827", display: "flex", alignItems: "center", gap: 8 },
    desc: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.65)" : "#6b7280", marginTop: 8, lineHeight: 1.55 },
    close: { cursor: "pointer", color: dark ? "rgba(248,250,252,0.6)" : "#9ca3af", display: "flex" },
    logout: { cursor: "pointer", color: dark ? "rgba(248,250,252,0.6)" : "#9ca3af", display: "flex", alignItems: "center", gap: 4, fontSize: "0.8rem", fontWeight: 600, flexShrink: 0, "&:hover": { color: dark ? "#f8fafc" : "#374151" } },
    headActions: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), flexShrink: 0 },
    support: { position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#25D366", textDecoration: "none", cursor: "pointer", transition: "color 150ms", "&:hover": { color: "#1da851" } },
    supportBadge: { position: "absolute", top: -5, right: -5, minWidth: 15, height: 15, padding: "0 3px", borderRadius: 8, background: "#fff", color: "#1da851", fontWeight: 800, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" },
    back: { cursor: "pointer", color: dark ? "rgba(248,250,252,0.7)" : "#6b7280", display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem" },
    // Step indicator
    steps: { display: "flex", alignItems: "center", gap: 6, padding: theme.spacing(2.5, 3.5, 2.5), marginTop: theme.spacing(1.5), borderTop: `1px solid ${border}`, borderBottom: `1px solid ${border}` },
    stepItem: { display: "flex", alignItems: "center", gap: 6, fontSize: "0.72rem", fontWeight: 500, color: dark ? "rgba(248,250,252,0.45)" : "#9ca3af" },
    stepItemActive: { color: "#fff" },
    stepDot: { width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.68rem", fontWeight: 700, lineHeight: 1, textAlign: "center", flexShrink: 0, background: dark ? "rgba(255,255,255,0.08)" : "#f3f4f6", color: "inherit" },
    stepDotActive: { background: "#1463ff", color: "#fff" },
    stepSep: { flex: 1, height: 1, background: border, minWidth: 8 },
    body: { padding: theme.spacing(2.5, 3.5, 3.5), overflowY: "auto" },
    tiers: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1.5) },
    tier: { border: `2px solid ${border}`, borderRadius: 12, padding: theme.spacing(1.5), cursor: "pointer", transition: "all 150ms" },
    tierActive: { borderColor: "#3D5CFD", background: dark ? "rgba(61,92,253,0.1)" : "rgba(61,92,253,0.05)" },
    tierName: { fontWeight: 700, fontSize: "1rem", color: dark ? "#f8fafc" : "#111827", display: "flex", alignItems: "center", gap: 6 },
    tierDesc: { fontSize: "0.78rem", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", marginTop: 4 },
    tierFeatures: { listStyle: "none", margin: theme.spacing(1.25, 0, 0), padding: 0, display: "flex", flexDirection: "column", gap: 5 },
    tierFeat: { display: "flex", alignItems: "flex-start", gap: 6, fontSize: "0.74rem", lineHeight: 1.3, color: dark ? "rgba(248,250,252,0.8)" : "#4b5563" },
    tierFeatIcon: { color: "#22c55e", flexShrink: 0, marginTop: 1 },
    aiHelp: { display: "inline-flex", alignItems: "center", color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af", cursor: "help", "&:hover": { color: dark ? "rgba(248,250,252,0.8)" : "#6b7280" } },
    // Seletor de ciclo = segmented control centralizado (track com fundo atrás
    // dos 2 botões), acima dos cards. O ativo "flutua" com fundo claro + sombra.
    cycleWrap: { display: "flex", justifyContent: "center", marginTop: theme.spacing(1.5), marginBottom: theme.spacing(0.5) },
    cycleTrack: { display: "inline-flex", gap: 3, padding: 3, borderRadius: 10, background: dark ? "rgba(255,255,255,0.05)" : "#f3f4f6", border: `1px solid ${border}` },
    cyclePill: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, padding: theme.spacing(0.55, 2), borderRadius: 8, cursor: "pointer", border: "none", background: "transparent", fontSize: "0.75rem", fontWeight: 600, fontFamily: "inherit", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", transition: "all 150ms", whiteSpace: "nowrap" },
    cyclePillActive: { background: dark ? "rgba(255,255,255,0.12)" : "#fff", color: dark ? "#f8fafc" : "#111827", fontWeight: 700, boxShadow: dark ? "0 1px 3px rgba(0,0,0,0.35)" : "0 1px 3px rgba(0,0,0,0.1)" },
    save: { color: "#22c55e", fontSize: "0.62rem", fontWeight: 700, background: dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.12)", borderRadius: 5, padding: "1px 5px", lineHeight: 1 },
    rowLabel: { fontSize: "0.85rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    hint: { fontSize: "0.74rem", color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af" },
    stepper: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
    stepBtn: { width: 34, height: 34, borderRadius: 8, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: dark ? "rgba(255,255,255,0.08)" : "#f3f4f6", color: dark ? "#f8fafc" : "#111827", "&:disabled": { opacity: 0.4, cursor: "not-allowed" } },
    stepVal: { minWidth: 36, textAlign: "center", fontSize: "1.1rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827" },
    addonRow: { display: "flex", alignItems: "center", gap: theme.spacing(1), cursor: "pointer" },
    checkbox: { width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${dark ? "rgba(255,255,255,0.3)" : "#d1d5db"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    checkboxOn: { background: "#3D5CFD", borderColor: "#3D5CFD", color: "#fff" },
    priceBox: { borderTop: `1px solid ${border}`, paddingTop: theme.spacing(2), display: "flex", alignItems: "flex-end", justifyContent: "space-between" },
    priceTotal: { fontSize: "1.9rem", fontWeight: 800, color: dark ? "#f8fafc" : "#111827", lineHeight: 1 },
    pricePer: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", fontWeight: 500 },
    priceBreak: { fontSize: "0.78rem", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", textAlign: "right" },
    annualNote: { fontSize: "0.76rem", color: "#22c55e", marginTop: 2 },
    cta: { ...md.mdBtnPrimary, height: 48, width: "100%", fontSize: "1rem", marginTop: theme.spacing(1), display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
    // Card preview
    cardPreview: { borderRadius: 14, padding: theme.spacing(2), color: "#fff", minHeight: 168, background: "linear-gradient(135deg, #142966 0%, #4c4e58 55%, #747474 100%)", display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: "'Montserrat', sans-serif" },
    cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    chip: { width: 42, height: 31, borderRadius: 6, background: "linear-gradient(135deg,#f9d976 0%,#caa64a 100%)", boxShadow: "inset 0 0 4px rgba(0,0,0,0.2)" },
    cardBrandBadge: { fontWeight: 800, fontSize: "1.15rem", letterSpacing: 1, fontStyle: "italic", textShadow: "0 1px 2px rgba(0,0,0,0.25)", minHeight: "1.2em" },
    cardNum: { fontSize: "1.3rem", letterSpacing: 2.5, fontWeight: 600, textShadow: "0 1px 3px rgba(0,0,0,0.3)", margin: theme.spacing(1.5, 0) },
    cardBottom: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 },
    cardMiniLabel: { fontSize: "0.58rem", opacity: 0.75, letterSpacing: 1, textTransform: "uppercase" },
    cardName: { fontSize: "0.82rem", fontWeight: 600, textTransform: "uppercase", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    cardExp: { fontSize: "0.82rem", fontWeight: 600 },
    brandsRow: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
    brandsLabel: { fontSize: "0.72rem", color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af", marginRight: 2 },
    brandChip: { fontSize: "0.6rem", fontWeight: 700, padding: "3px 7px", borderRadius: 5, border: `1px solid ${border}`, color: dark ? "rgba(248,250,252,0.55)" : "#6b7280", letterSpacing: 0.3, opacity: 0.55, transition: "all 120ms" },
    brandChipActive: { opacity: 1, borderColor: "#3D5CFD", color: "#3D5CFD", background: dark ? "rgba(61,92,253,0.14)" : "rgba(61,92,253,0.06)" },
    // Inputs
    field: { display: "flex", flexDirection: "column", gap: 5 },
    label: { fontSize: "0.8rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    input: { height: 42, borderRadius: 8, border: `1px solid ${border}`, background: dark ? "rgba(255,255,255,0.05)" : "#fff", color: dark ? "#f8fafc" : "#111827", padding: "0 12px", fontSize: "0.95rem", fontFamily: "inherit", outline: "none", "&:focus": { borderColor: "#3D5CFD" } },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1) },
    gridCepNum: { display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: theme.spacing(1) },
    summary: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.75)" : "#374151", background: dark ? "rgba(255,255,255,0.04)" : "#f9fafb", borderRadius: 8, padding: theme.spacing(1.25, 1.5) },
    error: { fontSize: "0.82rem", color: "#ef4444" },
    testNote: { fontSize: "0.74rem", color: dark ? "rgba(248,250,252,0.45)" : "#9ca3af" },
    center: { textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: theme.spacing(1.5), padding: theme.spacing(2, 0) },
    successIcon: { color: "#22c55e" },
    closeBtn: { marginTop: theme.spacing(1.5), background: "transparent", border: `1px solid ${border}`, color: dark ? "#f8fafc" : "#111827", borderRadius: 8, height: 40, padding: "0 22px", cursor: "pointer", fontSize: "0.88rem", fontWeight: 600, fontFamily: "inherit" },
    // Seletor de forma de pagamento (segmented, igual ao de ciclo).
    payToggle: { display: "inline-flex", gap: 3, padding: 3, borderRadius: 10, background: dark ? "rgba(255,255,255,0.05)" : "#f3f4f6", border: `1px solid ${border}` },
    payPill: { display: "inline-flex", alignItems: "center", gap: 5, padding: theme.spacing(0.55, 1.5), borderRadius: 8, cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", transition: "all 150ms", whiteSpace: "nowrap" },
    payPillActive: { background: dark ? "rgba(255,255,255,0.12)" : "#fff", color: dark ? "#f8fafc" : "#111827", fontWeight: 700, boxShadow: dark ? "0 1px 3px rgba(0,0,0,0.35)" : "0 1px 3px rgba(0,0,0,0.1)" },
    // Seção de pagamento: 2 cards (Cartão / PIX).
    payCards: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1.5) },
    payCardItem: { position: "relative", border: `2px solid ${border}`, borderRadius: 12, padding: theme.spacing(1.75, 1), cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, color: dark ? "rgba(248,250,252,0.8)" : "#374151", transition: "all 150ms", "&:hover": { borderColor: dark ? "rgba(255,255,255,0.25)" : "#cbd5e1" } },
    payCardActive: { borderColor: "#3D5CFD", background: dark ? "rgba(61,92,253,0.1)" : "rgba(61,92,253,0.05)", color: dark ? "#f8fafc" : "#111827" },
    payCardDisabled: { opacity: 0.45, cursor: "not-allowed", "&:hover": { borderColor: border } },
    payCardLabel: { fontSize: "0.85rem", fontWeight: 700 },
    payCardNote: { fontSize: "0.64rem", fontWeight: 600, color: "#7b2dff" },
    // PIX: QR + copia-e-cola.
    qrFrame: { alignSelf: "center", background: "#fff", padding: 12, borderRadius: 12, lineHeight: 0, boxShadow: "0 1px 6px rgba(0,0,0,0.12)" },
    pixAmount: { textAlign: "center", fontSize: "1.4rem", fontWeight: 800, color: dark ? "#f8fafc" : "#111827" },
    copyBox: { display: "flex", alignItems: "center", gap: 8, border: `1px solid ${border}`, borderRadius: 8, padding: theme.spacing(1, 1.25), background: dark ? "rgba(255,255,255,0.04)" : "#f9fafb" },
    copyText: { flex: 1, fontSize: "0.72rem", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace" },
    copyBtn: { display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, border: "none", borderRadius: 7, padding: "7px 11px", cursor: "pointer", fontSize: "0.76rem", fontWeight: 700, fontFamily: "inherit", background: "#3D5CFD", color: "#fff", "&:hover": { background: "#2f4ae0" } },
    // Transição suave entre etapas (avançar = desliza da direita; voltar = da esquerda).
    "@keyframes spin360": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    spin: { animation: "$spin360 0.9s linear infinite" },
    "@keyframes stepInFwd": { from: { opacity: 0, transform: "translateX(22px)" }, to: { opacity: 1, transform: "translateX(0)" } },
    "@keyframes stepInBack": { from: { opacity: 0, transform: "translateX(-22px)" }, to: { opacity: 1, transform: "translateX(0)" } },
    stepWrap: { display: "flex", flexDirection: "column", gap: theme.spacing(2.25) },
    animFwd: { animation: "$stepInFwd 280ms cubic-bezier(0.22,1,0.36,1)" },
    animBack: { animation: "$stepInBack 280ms cubic-bezier(0.22,1,0.36,1)" }
  };
});

const PlanCheckoutModal = () => {
  const classes = useStyles();
  const { billing, refetchBilling } = useOnboarding();
  const { handleLogout } = useContext(AuthContext);

  const [dismissed, setDismissed] = useState(false);
  const [step, setStep] = useState("select");
  const [dir, setDir] = useState(1); // 1 = avançar, -1 = voltar (direção da animação)
  const go = (next, direction = 1) => { setDir(direction); setStep(next); };
  const [tier, setTier] = useState("pro");
  const [ciclo, setCiclo] = useState("mensal");
  const [paymentMethod, setPaymentMethod] = useState("card"); // card | pix (pix só no anual)
  const [internosQtd, setInternosQtd] = useState(0); // assentos de Chat Interno escolhidos
  // Piso do seletor de USUÁRIOS (humanos): nunca menos que os humanos atuais do
  // grupo, e nunca menos que o necessário p/ atingir a cobrança mínima (3
  // cobráveis). No Pro a IA conta como 1 → bastam 2 humanos p/ 3 cobráveis; no
  // Basic precisam ser 3. Assim o número exibido casa com o que é pago (e com o
  // teto contratado, evitando pagar por 3 e o seats_contracted ficar 2).
  const MIN_BILLABLE_SEATS = 3;
  const minSeats = Math.max(
    Math.max(0, billing?.seats || 0),
    MIN_BILLABLE_SEATS - (tier === "pro" ? 1 : 0)
  );
  const [humanos, setHumanos] = useState(minSeats);
  const [quote, setQuote] = useState(null);
  const [bill, setBill] = useState({ document: "", phone: "", cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "" });
  const [card, setCard] = useState({ number: "", holder: "", expiry: "", cvv: "" });
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false); // sondando pago_ativo pós-checkout
  const [confirmed, setConfirmed] = useState(false);   // webhook confirmou → conta liberada
  const [pixData, setPixData] = useState(null);        // { qr_code, qr_code_url, amount_cents }
  const [pixLoading, setPixLoading] = useState(false);
  const [pixError, setPixError] = useState("");
  const [copied, setCopied] = useState(false);
  const debounceRef = useRef();
  // Trava reentrante de checkout: garante 1 ÚNICO POST /account/checkout em voo
  // (cartão OU PIX), imune a duplo-clique / re-render / navegação. É ref (não
  // state) pra ser SÍNCRONA — dois cliques no mesmo tick veem o lock já fechado,
  // antes de qualquer re-render desabilitar o botão.
  const checkoutLock = useRef(false);

  // PIX só existe no ANUAL — ao voltar p/ mensal, força cartão.
  useEffect(() => { if (ciclo !== "anual") setPaymentMethod("card"); }, [ciclo]);

  // Re-clampa o seletor quando o piso muda (troca de plano Basic/Pro ou a
  // contagem real do grupo) — nunca deixa abaixo do mínimo cobrável.
  useEffect(() => { setHumanos(h => Math.max(h, minSeats)); }, [minSeats]);

  // Chat Interno = add-on CONTRATADO, independente do atendimento e SEM mínimo
  // (começa em 0; o cliente clica + pra adicionar). 0 = sem add-on; o add-on
  // liga sozinho quando há ≥1 assento interno.
  const minInternos = 0;
  const addon = internosQtd > 0;

  // Número EXIBIDO no seletor = usuários cobráveis (inclui a IA no Pro). O
  // estado fonte é `humanos`; o backend recebe `humanos` e recalcula o cobrável.
  const seatsCobraveis = Math.max(MIN_BILLABLE_SEATS, humanos + (tier === "pro" ? 1 : 0));

  const loadQuote = useCallback(() => {
    api.post("/account/quote", { tier, ciclo, qtd_usuarios: humanos, qtd_internos: internosQtd, addon })
      .then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [tier, ciclo, humanos, internosQtd, addon]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadQuote, 250);
    return () => clearTimeout(debounceRef.current);
  }, [loadQuote]);

  // Pós-sucesso: SONDA o status "fresco" (pula cache) a cada 2,5s até confirmar
  // `pago_ativo` — cada sondagem dispara, no servidor, a reavaliação de
  // entitlement (e a auto-provisão da IA do Pro) assim que o webhook do Pagar.me
  // cair. Ao confirmar → mostra "Conta liberada!" e RECARREGA a app já ativa
  // (transição visível). Após ~40s sem confirmar → para o spinner e libera o
  // botão "Acessar agora" (o pagamento foi feito; a liberação pode estar só
  // atrasada no webhook) — nunca fica em spinner infinito.
  useEffect(() => {
    // Sonda tanto no cartão (etapa "success") quanto no PIX (etapa "pix",
    // enquanto o cliente paga o QR). No cartão para após ~40s (libera "Acessar");
    // no PIX segue sondando até pagar (ou o cliente sair) — pagar PIX leva mais.
    if (step !== "success" && step !== "pix") { setConfirming(false); setConfirmed(false); return undefined; }
    setConfirming(true);
    setConfirmed(false);
    let attempts = 0;
    let reloadTimer = null;
    const poll = async () => {
      attempts += 1;
      const b = refetchBilling ? await refetchBilling({ fresh: true }) : null;
      if (b && b.status === "pago_ativo") {
        clearInterval(iv);
        setConfirming(false);
        setConfirmed(true);
        // Conta liberada → navega pra /tickets (full load: reavalia o billing já
        // ativo, paywall some e a IA do Pro provisiona).
        reloadTimer = setTimeout(() => { window.location.assign("/tickets"); }, 1800);
      } else if (step === "success" && attempts >= 16) {
        clearInterval(iv);
        setConfirming(false); // tempo esgotou; deixa o usuário acessar mesmo assim
      }
    };
    const iv = setInterval(poll, 2500);
    poll();
    return () => { clearInterval(iv); if (reloadTimer) clearTimeout(reloadTimer); };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dismissed) return null;
  if (!billing) return null;
  // Paywall NÃO-FECHÁVEL: abre enquanto a conta está PAUSADA (trial acabou e
  // ainda sem pagamento). Mantém aberto nas etapas "success" e "pix" mesmo
  // depois de o billing liberar (paused=false) — senão o modal sumiria ao
  // confirmar o PIX, sem mostrar "Conta liberada!" antes do redirect.
  // (L7) Mantém o modal aberto durante TODO checkout em andamento (qualquer etapa
  // após "select"), mesmo que o billing reflita paused=false no meio (ex.: poll de
  // 10min) — senão o formulário sumiria no meio do preenchimento. As etapas
  // "success"/"pix" já dependiam disso (liberam paused antes do redirect).
  const checkoutInProgress = step !== "select";
  if (!billing.paused && !checkoutInProgress) return null;

  const t = k => i18n.t(`onboarding.checkout.${k}`);
  const annualTotal = ciclo === "anual" && quote?.valor_cents != null ? quote.valor_cents * 12 : null;
  const brand = detectBrand(card.number);

  const onNumber = v => setCard({ ...card, number: maskedNumber(v).replace(/•/g, "").trimEnd() });
  const onExpiry = v => { const d = onlyDigits(v).slice(0, 6); setCard({ ...card, expiry: d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d }); };

  // CEP → autopreenche via ViaCEP (best-effort).
  const onCep = async v => {
    const masked = fmtCep(v);
    setBill(b => ({ ...b, cep: masked }));
    const d = onlyDigits(v);
    if (d.length === 8) {
      try {
        const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
        const j = await r.json();
        if (!j.erro) setBill(b => ({ ...b, cep: masked, street: j.logradouro || b.street, neighborhood: j.bairro || b.neighborhood, city: j.localidade || b.city, state: j.uf || b.state }));
      } catch { /* digita manual */ }
    }
  };

  // PIX exige só documento (+telefone) — endereço não é necessário. Cartão exige
  // o endereço completo (AVS/antifraude do adquirente).
  const billingValid =
    onlyDigits(bill.document).length >= 11 &&
    onlyDigits(bill.phone).length >= 10 &&
    onlyDigits(bill.cep).length === 8 &&
    bill.street.trim() && bill.number.trim() && bill.city.trim() && bill.state.trim();

  const goToBilling = () => {
    setError(""); go("billing", 1);
  };

  // Seleção da forma de pagamento (etapa "card" = seção de pagamento, 2 cards).
  const selectCard = () => { setError(""); setPaymentMethod("card"); };
  const selectPix = () => {
    if (ciclo !== "anual") return; // PIX só no anual
    if (checkoutLock.current) return; // checkout em voo → ignora clique repetido
    setPaymentMethod("pix");
    go("pix", 1);
    handlePixCheckout(); // self-guard: reusa se já houver PIX, no-op se travado
  };

  // Cria a cobrança PIX no painel (via Chat) e guarda QR + copia-e-cola.
  // O lock reentrante + o reuso do PIX já gerado garantem que NUNCA se cria mais
  // de um pedido: duplo-clique, voltar-e-reselecionar ou retry não disparam um
  // segundo POST /account/checkout.
  const handlePixCheckout = async () => {
    if (checkoutLock.current) return; // já há um checkout em voo
    if (pixData) return;              // PIX já gerado → reusa o QR (não duplica o pedido)
    checkoutLock.current = true;
    setPixError(""); setCopied(false); setPixLoading(true);
    try {
      const { data } = await api.post("/account/checkout", {
        tier, ciclo: "anual", qtd_usuarios: humanos, qtd_internos: internosQtd,
        payment_method: "pix", accept_terms: true,
        document: onlyDigits(bill.document), phone: onlyDigits(bill.phone)
      });
      if (!data?.qr_code && !data?.qr_code_url) throw new Error("QR PIX indisponível");
      setPixData(data);
    } catch (e) {
      setPixData(null); // limpa p/ permitir um retry consciente (botão "tentar de novo")
      const detail = e?.response?.data?.detail;
      const msg = (detail && (detail.message || JSON.stringify(detail))) || e?.response?.data?.error || e.message;
      setPixError(t("payError") + (msg ? `: ${msg}` : ""));
    } finally {
      setPixLoading(false);
      checkoutLock.current = false;
    }
  };

  const goToPayment = () => {
    if (!billingValid) { setError(t("fillBilling")); return; }
    setError(""); go("card", 1); // etapa de pagamento (escolhe Cartão/PIX nos 2 cards)
  };

  const copyPix = () => {
    const code = pixData?.qr_code || "";
    if (!code) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    // (L6) Clipboard API quando disponível; senão cai no fallback execCommand —
    // nunca falha em silêncio (o QR segue como caminho primário de qualquer forma).
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => { if (legacyCopy(code)) done(); });
    } else if (legacyCopy(code)) {
      done();
    }
  };

  const handlePay = async () => {
    setError("");
    if (!card.number || !card.holder || onlyDigits(card.expiry).length < 4 || !card.cvv) { setError(t("fillCard")); return; }
    if (!hasPagarmePublicKey()) { setError(t("payError")); return; } // chave pública ausente (deploy) → erro claro, sem tokenizar
    if (checkoutLock.current) return; // já há um checkout em voo → bloqueia duplo-submit
    checkoutLock.current = true;
    setPaying(true);
    try {
      const token = await tokenizeCard(card);
      const billing_address = {
        line_1: `${bill.number}, ${bill.street}${bill.neighborhood ? ", " + bill.neighborhood : ""}`,
        line_2: bill.complement || undefined,
        zip_code: onlyDigits(bill.cep),
        city: bill.city,
        state: bill.state
      };
      await api.post("/account/checkout", {
        tier, ciclo, qtd_usuarios: humanos, qtd_internos: internosQtd, addon, card_token: token, accept_terms: true,
        document: onlyDigits(bill.document), phone: onlyDigits(bill.phone), billing_address
      });
      go("success", 1);
      refetchBilling && refetchBilling();
      setTimeout(() => refetchBilling && refetchBilling(), 4000);
      setTimeout(() => refetchBilling && refetchBilling(), 9000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && (detail.message || JSON.stringify(detail))) || e.message;
      setError(t("payError") + (msg ? `: ${msg}` : ""));
    } finally {
      setPaying(false);
      checkoutLock.current = false;
    }
  };

  const idx = STEP_INDEX[step];
  const stepLabels = [t("stepPlan"), t("stepBilling"), t("stepPayment")];

  return (
    <Dialog open disableEscapeKeyDown className={classes.dialog} maxWidth={false}>
      {step !== "success" && (
        <div className={classes.head}>
          <div>
            {(step === "billing" || step === "card" || step === "pix") && (
              <span className={classes.back} onClick={() => !paying && !pixLoading && go(step === "billing" ? "select" : step === "pix" ? "card" : "billing", -1)}>
                <ArrowLeft size={14} /> {t("back")}
              </span>
            )}
            <div className={classes.title} style={{ marginTop: step === "select" ? 0 : 6 }}>
              {step === "select" ? t("title") : step === "billing" ? t("billingTitle") : step === "pix" ? t("pixTitle") : t("cardTitle")}
            </div>
            <div className={classes.desc}>{step === "select" ? t("desc") : step === "billing" ? t("billingDesc") : step === "pix" ? t("pixDesc") : t("cardDesc")}</div>
          </div>
          <div className={classes.headActions}>
            {/* Só na etapa de plano: suporte da Sttelar no WhatsApp (nova aba). */}
            {step === "select" && (
              <a
                className={classes.support}
                href={WHATSAPP_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                title={i18n.t("onboarding.checkout.supportTooltip")}
              >
                <MessageCircle size={18} />
                <span className={classes.supportBadge}>?</span>
              </a>
            )}
            {/* Paywall não-fechável: sem X. Única saída sem pagar é deslogar. */}
            <span className={classes.logout} onClick={handleLogout} title={i18n.t("onboarding.checkout.logoutTooltip")}>
              <LogOut size={15} /> {i18n.t("onboarding.checkout.logout")}
            </span>
          </div>
        </div>
      )}

      {step !== "success" && (
        <div className={classes.steps}>
          {stepLabels.map((lbl, i) => (
            <React.Fragment key={lbl}>
              {i > 0 && <span className={classes.stepSep} />}
              <span className={`${classes.stepItem} ${idx === i ? classes.stepItemActive : ""}`}>
                <span className={`${classes.stepDot} ${idx >= i ? classes.stepDotActive : ""}`}>{idx > i ? <Check size={12} /> : i + 1}</span>
                {lbl}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className={classes.body}>
       <div key={step} className={`${classes.stepWrap} ${dir >= 0 ? classes.animFwd : classes.animBack}`}>
        {step === "select" && (
          <>
            <div className={classes.cycleWrap}>
              <div className={classes.cycleTrack}>
                <div className={`${classes.cyclePill} ${ciclo === "mensal" ? classes.cyclePillActive : ""}`} onClick={() => setCiclo("mensal")}>{t("monthly")}</div>
                <div className={`${classes.cyclePill} ${ciclo === "anual" ? classes.cyclePillActive : ""}`} onClick={() => setCiclo("anual")}>{t("annual")}<span className={classes.save}>{t("annualSave")}</span></div>
              </div>
            </div>
            <div className={classes.tiers}>
              {["basic", "pro"].map(tk => (
                <div key={tk} className={`${classes.tier} ${tier === tk ? classes.tierActive : ""}`} onClick={() => setTier(tk)}>
                  <div className={classes.tierName}>
                    {tk === "pro" && <Sparkles size={16} color="#7b2dff" />}{t(tk)}
                    {tk === "pro" && (
                      <Tooltip title={t("aiSeatTooltip")} arrow enterTouchDelay={0} placement="top">
                        <span className={classes.aiHelp} onClick={e => e.stopPropagation()}><HelpCircle size={13} /></span>
                      </Tooltip>
                    )}
                  </div>
                  <div className={classes.tierDesc}>{t(tk === "pro" ? "proDesc" : "basicDesc")}</div>
                  <ul className={classes.tierFeatures}>
                    {(Array.isArray(i18n.t(`onboarding.checkout.features.${tk}`, { returnObjects: true }))
                      ? i18n.t(`onboarding.checkout.features.${tk}`, { returnObjects: true })
                      : []
                    ).map((f, i) => (
                      <li key={i} className={classes.tierFeat}>
                        <Check size={13} className={classes.tierFeatIcon} /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div><div className={classes.rowLabel}>{t("users")}</div><div className={classes.hint}>{t("minNote")}</div></div>
              <div className={classes.stepper}>
                <button type="button" className={classes.stepBtn} disabled={humanos <= minSeats} onClick={() => setHumanos(h => Math.max(minSeats, h - 1))}><Minus size={16} /></button>
                <span className={classes.stepVal}>{seatsCobraveis}</span>
                <button type="button" className={classes.stepBtn} onClick={() => setHumanos(h => h + 1)}><Plus size={16} /></button>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div><div className={classes.rowLabel}>{t("addon")}</div><div className={classes.hint}>{t("addonHint")}</div></div>
              <div className={classes.stepper}>
                <button type="button" className={classes.stepBtn} disabled={internosQtd <= minInternos} onClick={() => setInternosQtd(n => Math.max(minInternos, n - 1))}><Minus size={16} /></button>
                <span className={classes.stepVal}>{internosQtd}</span>
                <button type="button" className={classes.stepBtn} onClick={() => setInternosQtd(n => n + 1)}><Plus size={16} /></button>
              </div>
            </div>
            <div className={classes.priceBox}>
              <div>
                <span className={classes.priceTotal}>{fmtBRL(quote?.valor_cents)}</span>
                <span className={classes.pricePer}> {t("perMonth")}</span>
                {/* Linha do anual SEMPRE renderizada (visibility) p/ reservar a
                    altura — evita o modal "pular" ao alternar mensal/anual. */}
                <div className={classes.annualNote} style={{ visibility: annualTotal != null ? "visible" : "hidden" }}>
                  {t("annualCharge")}: {fmtBRL(annualTotal != null ? annualTotal : 0)}
                </div>
              </div>
              {quote && (
                <div className={classes.priceBreak}>
                  {quote.billable_seats} {t("billable")}{quote.ai_seat ? i18n.t("onboarding.checkout.aiSeatSuffix") : ""}<br />
                  {quote.billable_seats} × {fmtBRL(quote.unit_cents)}{quote.addon_total_cents ? ` + ${fmtBRL(quote.addon_total_cents)}` : ""}
                </div>
              )}
            </div>
            <button type="button" className={classes.cta} onClick={goToBilling} disabled={!quote || quote.valor_cents == null}>{t("toBilling")} <ArrowRight size={18} /></button>
          </>
        )}

        {step === "billing" && (
          <>
            <div className={classes.grid2}>
              <div className={classes.field}>
                <span className={classes.label}>{t("document")}</span>
                <input className={classes.input} inputMode="numeric" placeholder={i18n.t("onboarding.checkout.documentPlaceholder")}
                  value={fmtDoc(bill.document)} onChange={e => setBill({ ...bill, document: onlyDigits(e.target.value) })} />
              </div>
              <div className={classes.field}>
                <span className={classes.label}>{t("phone")}</span>
                <input className={classes.input} inputMode="numeric" placeholder="(11) 99999-9999"
                  value={fmtPhone(bill.phone)} onChange={e => setBill({ ...bill, phone: onlyDigits(e.target.value) })} />
              </div>
            </div>
            <div className={classes.gridCepNum}>
              <div className={classes.field}>
                <span className={classes.label}>{t("cep")}</span>
                <input className={classes.input} inputMode="numeric" placeholder="00000-000"
                  value={fmtCep(bill.cep)} onChange={e => onCep(e.target.value)} />
              </div>
              <div className={classes.field}>
                <span className={classes.label}>{t("number")}</span>
                <input className={classes.input} placeholder={i18n.t("onboarding.checkout.numberPlaceholder")} value={bill.number} onChange={e => setBill({ ...bill, number: e.target.value })} />
              </div>
            </div>
            <div className={classes.field}>
              <span className={classes.label}>{t("street")}</span>
              <input className={classes.input} placeholder={i18n.t("onboarding.checkout.streetPlaceholder")} value={bill.street} onChange={e => setBill({ ...bill, street: e.target.value })} />
            </div>
            <div className={classes.grid2}>
              <div className={classes.field}>
                <span className={classes.label}>{t("complement")}</span>
                <input className={classes.input} placeholder={t("optional")} value={bill.complement} onChange={e => setBill({ ...bill, complement: e.target.value })} />
              </div>
              <div className={classes.field}>
                <span className={classes.label}>{t("neighborhood")}</span>
                <input className={classes.input} value={bill.neighborhood} onChange={e => setBill({ ...bill, neighborhood: e.target.value })} />
              </div>
            </div>
            <div className={classes.gridCepNum}>
              <div className={classes.field}>
                <span className={classes.label}>{t("city")}</span>
                <input className={classes.input} value={bill.city} onChange={e => setBill({ ...bill, city: e.target.value })} />
              </div>
              <div className={classes.field}>
                <span className={classes.label}>{t("state")}</span>
                <input className={classes.input} maxLength={2} placeholder={i18n.t("onboarding.checkout.statePlaceholder")} value={bill.state} onChange={e => setBill({ ...bill, state: e.target.value.toUpperCase() })} />
              </div>
            </div>
            {error && <div className={classes.error}>{error}</div>}
            <button type="button" className={classes.cta} onClick={goToPayment}>{t("toPayment")} <ArrowRight size={18} /></button>
          </>
        )}

        {step === "card" && (
          <>
            {/* 2 cards: Cartão de crédito ou PIX (PIX só no anual). */}
            <div className={classes.payCards}>
              <div className={`${classes.payCardItem} ${paymentMethod === "card" ? classes.payCardActive : ""}`} onClick={selectCard}>
                <CreditCard size={22} />
                <div className={classes.payCardLabel}>{t("payCard")}</div>
              </div>
              <div
                className={`${classes.payCardItem} ${paymentMethod === "pix" ? classes.payCardActive : ""} ${ciclo !== "anual" ? classes.payCardDisabled : ""}`}
                onClick={selectPix}
                title={ciclo !== "anual" ? t("pixOnlyAnnualShort") : undefined}
              >
                <QrCode size={22} />
                <div className={classes.payCardLabel}>{t("payPix")}</div>
                {ciclo !== "anual" && <div className={classes.payCardNote}>{t("pixOnlyAnnualShort")}</div>}
              </div>
            </div>

            {paymentMethod === "card" && (
            <>
            <div className={classes.summary}>
              {t(tier)} · {ciclo === "anual" ? t("annual") : t("monthly")} · {quote?.billable_seats} {t("billable")} — <strong>{fmtBRL(quote?.valor_cents)} {t("perMonth")}</strong>
              {annualTotal != null && <> ({fmtBRL(annualTotal)}/ano)</>}
            </div>
            <div className={classes.cardPreview}>
              <div className={classes.cardTop}>
                <div className={classes.chip} />
                <div className={classes.cardBrandBadge}>{brand ? (ACCEPTED_BRANDS.find(b => b.key === brand)?.label || "") : ""}</div>
              </div>
              <div className={classes.cardNum}>{maskedNumber(card.number)}</div>
              <div className={classes.cardBottom}>
                <div><div className={classes.cardMiniLabel}>{t("cardHolder")}</div><div className={classes.cardName}>{card.holder || i18n.t("onboarding.checkout.cardHolderPreview")}</div></div>
                <div style={{ textAlign: "right" }}><div className={classes.cardMiniLabel}>{t("expiry")}</div><div className={classes.cardExp}>{card.expiry || "MM/AA"}</div></div>
              </div>
            </div>
            <div className={classes.brandsRow}>
              <span className={classes.brandsLabel}>{t("acceptedBrands")}:</span>
              {ACCEPTED_BRANDS.map(b => <span key={b.key} className={`${classes.brandChip} ${brand === b.key ? classes.brandChipActive : ""}`}>{b.label}</span>)}
            </div>
            <div className={classes.field}>
              <span className={classes.label}>{t("cardNumber")}</span>
              <input className={classes.input} inputMode="numeric" autoComplete="cc-number" placeholder="0000 0000 0000 0000"
                value={maskedNumber(card.number).replace(/•/g, "").trimEnd()} onChange={e => onNumber(e.target.value)} />
            </div>
            <div className={classes.field}>
              <span className={classes.label}>{t("cardHolder")}</span>
              <input className={classes.input} autoComplete="cc-name" placeholder={i18n.t("onboarding.checkout.cardHolderPlaceholder")}
                value={card.holder} onChange={e => setCard({ ...card, holder: e.target.value.toUpperCase() })} />
            </div>
            <div className={classes.grid2}>
              <div className={classes.field}>
                <span className={classes.label}>{t("expiry")}</span>
                <input className={classes.input} inputMode="numeric" autoComplete="cc-exp" placeholder="MM/AA" value={card.expiry} onChange={e => onExpiry(e.target.value)} />
              </div>
              <div className={classes.field}>
                <span className={classes.label}>{t("cvv")}</span>
                <input className={classes.input} inputMode="numeric" autoComplete="cc-csc" placeholder="123" maxLength={4} value={card.cvv} onChange={e => setCard({ ...card, cvv: onlyDigits(e.target.value) })} />
              </div>
            </div>
            {error && <div className={classes.error}>{error}</div>}
            <button type="button" className={classes.cta} onClick={handlePay} disabled={paying}>
              {paying ? <Loader size={18} /> : <CreditCard size={18} />}
              {paying ? t("processing") : `${t("pay")} ${fmtBRL(annualTotal != null ? annualTotal : quote?.valor_cents)}`}
            </button>
            <div className={classes.testNote}>{t("secureNote")}</div>
            </>
            )}
          </>
        )}

        {step === "pix" && (
          <>
            <div className={classes.summary}>
              {t(tier)} · {t("annual")} · {quote?.billable_seats} {t("billable")} — <strong>{fmtBRL(pixData?.amount_cents ?? annualTotal)}/{t("annual").toLowerCase()}</strong>
            </div>
            {pixLoading ? (
              <div className={classes.center}><Loader size={26} className={classes.spin} /><div className={classes.testNote}>{t("processing")}</div></div>
            ) : pixError ? (
              <>
                <div className={classes.error}>{pixError}</div>
                <button type="button" className={classes.cta} onClick={handlePixCheckout}><QrCode size={18} /> {t("toPayment")}</button>
              </>
            ) : pixData ? (
              <>
                {confirmed ? (
                  <div className={classes.center}>
                    <CheckCircle2 size={56} className={classes.successIcon} />
                    <div className={classes.rowLabel} style={{ fontSize: "1.05rem" }}>{t("releasedTitle")}</div>
                    <div className={classes.desc} style={{ textAlign: "center" }}>{t("releasedDesc")}</div>
                  </div>
                ) : (
                  <>
                    <div className={classes.qrFrame}>
                      {pixData.qr_code
                        ? <QRCode value={pixData.qr_code} size={196} level="M" renderAs="svg" />
                        : <img src={pixData.qr_code_url} alt={i18n.t("onboarding.checkout.pixQrAlt")} width={196} height={196} />}
                    </div>
                    <div className={classes.pixAmount}>{fmtBRL(pixData.amount_cents)}</div>
                    {pixData.qr_code && (
                      <div className={classes.copyBox}>
                        <span className={classes.copyText}>{pixData.qr_code}</span>
                        <button type="button" className={classes.copyBtn} onClick={copyPix}>
                          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? t("pixCopied") : t("pixCopy")}
                        </button>
                      </div>
                    )}
                    <div className={classes.testNote} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      <Loader size={13} className={classes.spin} /> {t("pixWaiting")}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </>
        )}

        {step === "success" && (
          <div className={classes.center}>
            {confirming ? (
              <Loader size={52} className={classes.spin} />
            ) : (
              <CheckCircle2 size={56} className={classes.successIcon} />
            )}
            <div className={classes.rowLabel} style={{ fontSize: "1.05rem" }}>
              {confirmed ? t("releasedTitle") : confirming ? t("confirmingTitle") : t("successTitle")}
            </div>
            <div className={classes.desc} style={{ textAlign: "center" }}>
              {confirmed ? t("releasedDesc") : confirming ? t("releasingDesc") : t("stillReleasingDesc")}
            </div>
            <button
              type="button"
              className={classes.closeBtn}
              style={confirmed ? { background: "#22c55e", borderColor: "#22c55e", color: "#fff" } : undefined}
              onClick={() => window.location.assign("/tickets")}
            >
              {confirmed ? t("access") : t("closeNow")}
            </button>
          </div>
        )}
       </div>
      </div>
    </Dialog>
  );
};

export default PlanCheckoutModal;
